import {
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_APP_SET_LOADING_PROGRESS,
  EVENT_APP_TERMINATING,
  OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { translate, TranslationKey } from "@monsterverse/i18n";

import { PCAppEventHandler } from "./PCAppEventHandler";

type LoadingStage = { str: TranslationKey; start: number };

export class LoadingProgress {
  private progressStages: LoadingStage[] = [
    {
      str: "common.loading.loading_island",
      start: 0.0,
    },
    {
      str: "common.loading.loading_monsters",
      start: 0.55,
    },
    {
      str: "common.loading.loading_finished",
      start: 1.0,
    },
  ];

  private displayedProgress = 0.0;
  private actualProgress = 0.0;
  private previousTime = 0.0;
  private loadingEl: HTMLElement;
  private progressBarEl: HTMLElement;
  private nextAnimationFrame = 0; // If set at runtime, this should be a non-zero value.

  constructor(loadingEl: HTMLElement, pcAppEventHandler: PCAppEventHandler) {
    this.loadingEl = loadingEl;
    this.progressBarEl = loadingEl.querySelector(".progress-bar") as HTMLElement;
    this.setLoadingProgress(0);
    this.resetRendering();

    pcAppEventHandler.on(EVENT_APP_LOADING_COMPLETE, () => {
      this.setLoadingProgress(2);
    });

    pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, () => {
      this.cancelDraw();
      this.hide();
    });

    pcAppEventHandler.on(EVENT_APP_SET_LOADING_PROGRESS, (stage: unknown) => {
      this.setLoadingProgress(stage as number);
    });

    pcAppEventHandler.on(EVENT_APP_TERMINATING, (data: unknown) => {
      const handler = data as OnAppTerminatingData;
      if (handler) {
        const { exitingMonsterverse, customizer } = handler;
        if (!exitingMonsterverse && !customizer) {
          this.show();
          this.setLoadingProgress(0);
        }
      }
    });
  }

  setLoadingProgress(stage: number) {
    if (!this.progressBarEl?.firstElementChild) return;
    const { str, start } = this.progressStages[stage];
    this.progressBarEl.firstElementChild.textContent = translate(str) || "Loading island…";
    this.actualProgress = start;
  }

  private requestDraw() {
    this.nextAnimationFrame = requestAnimationFrame((t) => this.draw(t));
  }

  private cancelDraw() {
    if (this.nextAnimationFrame) cancelAnimationFrame(this.nextAnimationFrame);
  }

  private resetRendering() {
    this.actualProgress = 0;
    this.displayedProgress = 0;

    this.cancelDraw();
    this.requestDraw();
  }

  private show() {
    this.loadingEl.classList.remove("display-none");
    this.loadingEl.classList.remove("opacity0");

    this.resetRendering();
  }

  private hide() {
    const setDisplayNone = () => this.loadingEl.classList.add("display-none");
    const onHide = () => {
      clearTimeout(backupElementRemoval);
      setDisplayNone();
      this.loadingEl.removeEventListener("transitionend", onHide);
    };

    const backupElementRemoval = setTimeout(() => setDisplayNone(), 250);
    this.loadingEl.addEventListener("transitionend", onHide);
    this.loadingEl.classList.add("opacity0");
  }

  private draw(t: number) {
    if (!this.previousTime) {
      this.previousTime = t;
      this.requestDraw();
      return;
    }

    const delta = t - this.previousTime;
    this.previousTime = t;

    if (this.displayedProgress < this.actualProgress) {
      // Quickly advance progress bar as progress has advanced past current point
      this.displayedProgress += delta / 2000;

      // Eventually we want displayedProgress to catch up to actualProgress.
      if (this.actualProgress - this.displayedProgress < 0.001) {
        this.displayedProgress = this.actualProgress;
      }
    } else {
      // Creep progress bar slowly until progress advances to the next stage
      const nextStageStart =
        this.progressStages.find((s) => {
          return s.start > this.actualProgress;
        })?.start ?? 1;

      this.displayedProgress += this.ease(nextStageStart - this.displayedProgress) * (delta / 16000);
    }

    this.progressBarEl.style.setProperty("--progress", `${this.displayedProgress * 100}%`);
    if (this.displayedProgress < 1) {
      this.requestDraw();
    } else {
      this.hide();
    }
  }

  private ease(x: number) {
    return 1 - (1 - x) * (1 - x); // Quadratic Ease-out
  }
}
